/**
 * Stand-in data while the UI is built ahead of the API (Ray, 2026-08-28:
 * "lets start with UI first to make it fast, no backend api yet").
 *
 * Kept in one file, shaped exactly like the server models, so swapping it for
 * real calls is changing where the data comes from and nothing else. When the
 * endpoints land this file is deleted, not adapted.
 */

/**
 * A CUSTOMER's lifecycle is an approval, not a connection state (Ray,
 * 2026-08-28). `pending` only happens when somebody signs up through a public
 * form; an operator adding a customer in their own dashboard has approved them
 * by the act of typing them in, so the form does not ask.
 *
 * Suspended and disconnected are ACCOUNT states — see IspAccountStatus. They
 * have to be, because one branch of a business can be cut off for non-payment
 * while another keeps running.
 */
export type IspCustomerStatus = 'pending' | 'approved' | 'rejected';

/** One connection's state. This is where being cut off actually lives. */
export type IspAccountStatus =
   | 'waiting'
   | 'active'
   | 'suspended'
   | 'disconnected';

export interface IspCustomer {
   id: string;
   customerCode: string;
   fullName: string;
   phone: string;
   email: string;
   address: string;
   /** Which government ID they presented. */
   idType: IspIdType | '';
   /** Uploaded photo of it. */
   idPhotoUrl: string;
   status: IspCustomerStatus;
   notes: string;
   /** How many subscribed plans this customer holds. */
   accountCount: number;
}

/**
 * The IDs Philippine operators actually get handed. Ordered by how often they
 * turn up, so the common ones are not buried under a scroll.
 */
export const ID_TYPES = [
   'national-id',
   'drivers-license',
   'sss',
   'umid',
   'philhealth',
   'pagibig',
   'passport',
   'postal-id',
   'voters-id',
   'tin',
   'senior-id',
   'other',
] as const;

export type IspIdType = (typeof ID_TYPES)[number];

export const ID_TYPE_LABEL: Record<IspIdType, string> = {
   'national-id': 'National ID (PhilSys)',
   'drivers-license': "Driver's License",
   sss: 'SSS',
   umid: 'UMID',
   philhealth: 'PhilHealth',
   pagibig: 'Pag-IBIG',
   passport: 'Passport',
   'postal-id': 'Postal ID',
   'voters-id': "Voter's ID",
   tin: 'TIN',
   'senior-id': 'Senior Citizen ID',
   other: 'Other',
};

export const MOCK_CUSTOMERS: IspCustomer[] = [
   {
      id: 'c1',
      customerCode: 'C-1001',
      fullName: 'Rolando Mendoza',
      phone: '0917 555 0142',
      email: 'rolando.m@example.com',
      address: '14 Mabini St, Cabanatuan',
      idType: 'national-id',
      idPhotoUrl: '',
      status: 'approved',
      notes: '',
      accountCount: 1,
   },
   {
      // Aztech's own example: one customer, two branches, two accounts.
      id: 'c2',
      customerCode: 'C-1002',
      fullName: 'Marites Bautista',
      phone: '0918 555 0199',
      email: 'marites@bautistafoods.ph',
      address: 'Bautista Foods — Resto 1 & Resto 2',
      idType: 'drivers-license',
      idPhotoUrl: '',
      status: 'approved',
      notes: 'Two branches. Bills together.',
      accountCount: 2,
   },
   {
      id: 'c3',
      customerCode: 'C-1003',
      fullName: 'Jun Villanueva',
      phone: '0920 555 0177',
      email: '',
      address: '88 Rizal Ave, Talavera',
      idType: 'sss',
      idPhotoUrl: '',
      status: 'approved',
      notes: 'Two months behind — one account suspended.',
      accountCount: 1,
   },
   {
      id: 'c4',
      customerCode: 'C-1004',
      fullName: 'Kristine Dela Cruz',
      phone: '0915 555 0163',
      email: 'kdc@example.com',
      address: 'Purok 3, San Jose',
      idType: '',
      idPhotoUrl: '',
      status: 'pending',
      notes: 'Signed up on the public form. Installation booked Friday.',
      accountCount: 0,
   },
   {
      id: 'c5',
      customerCode: 'C-1005',
      fullName: 'Ernesto Ramos',
      phone: '0999 555 0121',
      email: '',
      address: '7 Bonifacio St, Guimba',
      idType: 'umid',
      idPhotoUrl: '',
      status: 'approved',
      notes: 'Moved away Aug 2026 — accounts disconnected.',
      accountCount: 0,
   },
];

export const STATUS_LABEL: Record<IspCustomerStatus, string> = {
   pending: 'Pending',
   approved: 'Approved',
   rejected: 'Rejected',
};

export const ACCOUNT_STATUS_LABEL: Record<IspAccountStatus, string> = {
   waiting: 'Waiting',
   active: 'Active',
   suspended: 'Suspended',
   disconnected: 'Disconnected',
};

/* ---------- Plans ---------- */

export interface IspPlan {
   id: string;
   /**
    * The MikroTik this plan runs on — MAY BE EMPTY.
    *
    * Operators write their price list before registering a router at least as
    * often as the other way round (Ray, 2026-08-31), so a plan with no server
    * is a valid, saveable thing. The link is filled in later, from either end.
    */
   server: string;
   name: string;
   price: number;
   speedLabel: string;
   /** The /ppp/profile on the MikroTik that actually enforces this plan. */
   routerProfile: string;
   /** RouterOS max-limit, e.g. "50M/50M". Stored as typed — RouterOS accepts
    *  the string directly and re-deriving it invites unit bugs. */
   bandwidth: string;
   burstMax: string;
   burstThreshold: string;
   burstSeconds: number;
   localAddress: string;
   remoteAddress: string;
   /**
    * Days after the due date before an account on THIS plan is cut off.
    * Per plan, not per business (Ray, 2026-08-28): a business package and a
    * ₱699 home line do not get the same patience.
    */
   graceDays: number;
   category: string;
   remarks: string;
}

export const MOCK_PLANS: IspPlan[] = [
   {
      id: 'p1',
      // Unlinked, which is exactly where an operator who wrote their price
      // list before registering a MikroTik would be.
      server: '',
      name: 'Home 25',
      price: 799,
      speedLabel: '25 Mbps',
      routerProfile: 'plan799',
      bandwidth: '25M/25M',
      burstMax: '30M/30M',
      burstThreshold: '20M/20M',
      burstSeconds: 8,
      localAddress: '10.20.0.1',
      remoteAddress: 'pppoe-pool',
      graceDays: 3,
      category: 'Residential',
      remarks: '',
   },
   {
      id: 'p2',
      // Unlinked, which is exactly where an operator who wrote their price
      // list before registering a MikroTik would be.
      server: '',
      name: 'Home 50',
      price: 1299,
      speedLabel: '50 Mbps',
      routerProfile: 'plan1299',
      bandwidth: '50M/50M',
      burstMax: '60M/60M',
      burstThreshold: '40M/40M',
      burstSeconds: 8,
      localAddress: '10.20.0.1',
      remoteAddress: 'pppoe-pool',
      graceDays: 3,
      category: 'Residential',
      remarks: '',
   },
   {
      id: 'p3',
      // Unlinked, which is exactly where an operator who wrote their price
      // list before registering a MikroTik would be.
      server: '',
      name: 'Business 100',
      price: 3499,
      speedLabel: '100 Mbps',
      routerProfile: 'biz100',
      bandwidth: '100M/100M',
      burstMax: '120M/120M',
      burstThreshold: '80M/80M',
      burstSeconds: 10,
      localAddress: '10.20.0.1',
      remoteAddress: 'biz-pool',
      graceDays: 7,
      category: 'Business',
      remarks: 'Longer grace — they pay on invoice terms.',
   },
];

/* ---------- Payment methods ---------- */

export const PAYMENT_KINDS = ['gcash', 'maya', 'bank', 'cash', 'other'] as const;
export type IspPaymentKind = (typeof PAYMENT_KINDS)[number];

export const PAYMENT_KIND_LABEL: Record<IspPaymentKind, string> = {
   gcash: 'GCash',
   maya: 'Maya',
   bank: 'Bank transfer',
   cash: 'Cash',
   other: 'Other',
};

export interface IspPaymentMethod {
   id: string;
   kind: IspPaymentKind;
   /** Account name, or what to write on the envelope for cash. */
   accountName: string;
   /** Number, or bank account number. Empty for cash. */
   accountNumber: string;
   /** Uploaded QR, for the wallets that have one. */
   qrUrl: string;
   instructions: string;
   /** Off methods stay on file but are not shown to a customer. */
   active: boolean;
}

export const MOCK_PAYMENT_METHODS: IspPaymentMethod[] = [
   {
      id: 'pm1',
      kind: 'gcash',
      accountName: 'Aztech Fiber',
      accountNumber: '0917 555 0142',
      qrUrl: '',
      instructions: 'Send the screenshot after paying.',
      active: true,
   },
   {
      id: 'pm2',
      kind: 'cash',
      accountName: 'Office — Mabini St',
      accountNumber: '',
      qrUrl: '',
      instructions: 'Mon–Sat, 8am to 5pm.',
      active: true,
   },
];
